import CircularDotsLoader from "@/components/common/CircularLoader";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";
import { authHeaders, Axios } from "@/utils/axios";
import { Role } from "@/utils/constant";
import { ApplicationStatus, ContractStatus, MilestoneStatus, NotificationHeader, NotificationName } from "@/utils/enum";
import { API_END_POINTS } from "@/utils/routes";
import { Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface notification {
    _id: string
    recipient: string,
    type: string,
    data: notificationData,
    read: boolean,
    createdAt: string,
    __v: string,
    navigationUrl?: string,
}

interface notificationData {
    applicationId?: string,
    status?: number,
    jobId?: string,
    senderName?: string,
    jobTitle?: string,
    contractId?: string,
    milestoneId?: string,
    milestoneTitle?: string
    milestoneTitles?: string[],
}

interface notificationProps {
    notificationCount: number
}

const NotificationPopover = ({ notificationCount }: notificationProps) => {
    const { user } = useUserStore();
    const { toast } = useToast();
    const navigate = useNavigate();
    const [allNotification, setAllNotification] = useState(null);
    const [isLoading, setIsShowLoader] = useState(false);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        getAllNotification();
    }, [notificationCount])


    const getAllNotification = async () => {
        try {
            const response = await Axios.get(
                `${API_END_POINTS.GET_ALL_NOTIFICATION_LIST}${user?.userId}`,
                authHeaders(user.accessToken)
            );
            if (response && response?.data) {
                let allNotification: notification[] = response?.data;
                allNotification = allNotification?.map((notification) => ({
                    ...notification,
                    header: getNotificationHeader(notification?.type),
                    notificationMessage: notificationMessage(notification),
                    timeAgo: getRelativeTime(notification.createdAt),
                    navigationUrl: navigateUrl(notification)
                }));
                setAllNotification(allNotification);
            } else {
                setAllNotification(null);
            }
        } catch (err: any) {
            setAllNotification(null);
            console.log('notificaiton not loadded', err);
        }
    }

    const patchRecentTime = () => {
        const allNotificationRecent = allNotification?.map((notification) => ({
            ...notification,
            timeAgo: getRelativeTime(notification.createdAt),
        }));
        setAllNotification(allNotificationRecent);
    }


    const notificationMessage = (notification: notification) => {
        const { type, data } = notification;

        if (type.includes("milestone_created")) {
            return `A milestone '${data?.milestoneTitles && data?.milestoneTitles[0] ? data?.milestoneTitles[0] : ''}' has been created for your contract.`
        }

        if (type.includes("milestone_update")) {
            // in-progress
            if(data?.status === MilestoneStatus.IN_PROGRESS){
                return `${data.senderName ?? "Someone"} started working on milestone '${data.milestoneTitle}'.`
            }
            // submitted
            if(data?.status === MilestoneStatus.SUBMITTED){
                return `${data.senderName ?? "Someone"} submitted milestone '${data.milestoneTitle}' for review.`
            }
            // reviewed
            if(data?.status === MilestoneStatus.REVIEWED){
                return `The client reviewed your milestone '${data.milestoneTitle}'.`
            }
            // needs-rework
            if(data?.status === MilestoneStatus.NEEDS_REWORK){
                return `You requested changes to the milestone '${data.milestoneTitle}'.`
            }
            // rejected
            if(data?.status === MilestoneStatus.REJECTED){
                return `The client rejected your milestone '${data.milestoneTitle}'.`
            }
            // approved
            if(data?.status === MilestoneStatus.APPROVED){
                return `Your milestone '${data.milestoneTitle}' has been approved!`
            }
        }

        if (type.includes("application_created")) {
            return `${data.senderName ?? "Someone"} has applied for your job: ${data.jobTitle}.`
        }

        if (type.includes("application_update")) {
            // Viewed
            if(data?.status === ApplicationStatus.VIEWED){
                return `The client viewed your application for ${data.jobTitle}.`
            }
            // Interview
            if(data?.status === ApplicationStatus.INTERVIEW){
                return `You’ve been invited for an interview for ${data.jobTitle}.`
            }
            // Accepted
            if(data?.status === ApplicationStatus.ACCEPTED){
                return `Your application for ${data.jobTitle} has been accepted!`
            }
            // On Hold
            if(data?.status === ApplicationStatus.ONHOLD){
                return `Your application for ${data.jobTitle} is on hold.`
            }
            // Rejected
            if(data?.status === ApplicationStatus.REJECTED){
                return `Your application for ${data.jobTitle} was not selected.`
            }
            // Withdrawn
            if(data?.status === ApplicationStatus.WITHDRAWN){
                return `${data.senderName ?? "Someone"} withdrew their application for ${data.jobTitle}.`
            }
        }

        if (type.includes("contract_created")) {
            return `The client has initiated a contract with you for ${data.jobTitle}.`
        }

        if (type.includes("contract_update")) {
            //Active
            if(data?.status === ContractStatus.ACCEPTED){
                return `${data.senderName ?? "Someone"} accepted your contract for ${data.jobTitle}.`;
            }
            // In Progress
            if(data?.status === ContractStatus.INITIATED){
                return `The contract for ${data.jobTitle} is now in progress.`;
            }
            // Completed
            if(data?.status === ContractStatus.COMPLETED){
                return `Contract for ${data.jobTitle} completed! Great work.`;
            }
            // Cancelled
            if(data?.status === ContractStatus.CANCELLED){
                return `The client has cancelled the contract for ${data.jobTitle}.`;
            }
            // Rejected
            if(data?.status === ContractStatus.REJECTED){
                return `${data.senderName ?? "Someone"} rejected your contract for ${data.jobTitle}.`;
            }
        }

        return "You have a new notification.";
    };


    const getNotificationHeader = (type: string) => {
        if (type.includes("milestone")) return NotificationHeader.MILESTONE;
        if (type.includes("application")) return NotificationHeader.APPLICATION;
        return NotificationHeader.CONTRACT;
    };

    const navigateUrl = (notification: notification) => {
        if (user.role === Role.ENTERPRISE) {
            if (notification?.type.includes("application")) return `/job-details/${notification?.data?.jobId}?application=application`;
        } else {
            if (notification?.type.includes("application")) return `/project-details/${notification?.data?.jobId}`;
        }
        return '/contracts';
    };

    const getRelativeTime = (dateString: string) => {
        const created = new Date(dateString).getTime();
        const now = Date.now();
        const diffMs = now - created;
        const minutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (days > 0) {
            return days === 1 ? "1 day ago" : `${days} days ago`;
        }
        if (hours > 0) {
            return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
        }
        if (minutes > 0) {
            return minutes === 1 ? "1 min ago" : `${minutes} mins ago`;
        }
        return "just now";
    };

    const markReadSingleNotification = async (notificationData: notification) => {
        try {
            setIsShowLoader(true);
            await Axios.put(
                `${API_END_POINTS.GET_ALL_NOTIFICATION_LIST}${notificationData?._id}/mark-read`,
                {},
                authHeaders(user.accessToken)
            );
            toast({
                title: "Notification marked as read",
                variant: "destructive",
            });
            setIsShowLoader(false);
        } catch (err: any) {
            setIsShowLoader(false);
            toast({
                title: "Failed to mark motification as read",
                variant: "destructive",
            });
        }
    }

    const markReadAllNotification = async () => {
        if (!allNotification || allNotification?.length === 0 || allNotification.every(n => n.read)) {
            return;
        }
        try {
            setIsShowLoader(true);
            await Axios.put(
                `${API_END_POINTS.GET_ALL_NOTIFICATION_LIST}${user?.userId}/mark-all-read`,
                {},
                authHeaders(user.accessToken)
            );
            toast({
                title: "All notifications marked as read",
                variant: "destructive",
            });
            setIsShowLoader(false);
        } catch (err: any) {
            setIsShowLoader(false);
            toast({
                title: "Failed to mark all motification as read",
                variant: "destructive",
            });
        }
    }

    const redirectToRespectivePage = (notificationData: notification) => {
        if (notificationData?.read) {
            return;
        }
        setOpen(false);
        navigate(notificationData?.navigationUrl);
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button onClick={() => patchRecentTime()}>
                    <Bell className="h-5 w-5" />
                    {notificationCount > 0 && <span className="absolute -top-0 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {notificationCount}
                    </span>}
                </button>
            </PopoverTrigger>

            <PopoverContent className="w-96 p-0">
                {isLoading && <CircularDotsLoader height={80} width={80} className="absolute" text="Loading...." />}
                <div className="flex items-center justify-between border-b px-6 py-4">
                    <h3 className="font-medium">Notifications</h3>
                    <span className={`text-xs ${!allNotification || allNotification?.length === 0 || allNotification.every(n => n.read)
                        ? "opacity-40"
                        : "cursor-pointer hover:underline text-gray-500"
                        }`} onClick={() => markReadAllNotification()}>
                        Mark all as read
                    </span>
                </div>
                <div className="min-h-[250px] max-h-80 max-w-100 overflow-y-auto divide-y">
                    {allNotification && allNotification?.length > 0 ? allNotification.map((n) => (
                        <div
                            key={n._id}
                            className={`flex gap-3 p-4 items-start 
                            ${n.read ? "opacity-50 " : "hover:bg-gray-50 cursor-pointer"}`}
                            onClick={() => redirectToRespectivePage(n)}
                        >
                            <div className="flex-1 text-sm min-w-0">
                                <p className="font-medium bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-transparent">{n.header}</p>
                                <p className="text-gray-700 break-words whitespace-normal">{n?.type.includes("application_created") ? NotificationName.JOB_APPLIED : n?.type.includes("contract_created") ? NotificationName.CONTRACT_CREATED : n?.type.includes("milestone_created") ? NotificationName.MILESTONE_CREATED : NotificationName.STATUS_UPDATED} </p>
                                {n.notificationMessage && (
                                    <p className="text-gray-500 text-xs break-words whitespace-normal">
                                        {n.notificationMessage}
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col items-end gap-1">
                                <span className="text-xs text-gray-400 whitespace-nowrap">{n.timeAgo}</span>
                                {!n.read && <button
                                    className="text-[11px] text-blue-600 hover:underline"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        markReadSingleNotification(n);
                                    }}
                                >
                                    Mark as read
                                </button>}
                            </div>
                        </div>
                    )) :
                        <div className="flex items-center min-h-[200px] justify-center p-4 text-center text-gray-500 text-sm">
                            No notifications found
                        </div>
                    }
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default NotificationPopover;
